import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  makeStyles,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Link,
  Button,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useApi } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { homepageLinksApiRef } from '../../api';
import { HomepageLink } from '../../types';
import { CollapsibleSection } from '../CollapsibleSection';
import { LinkContributionDialog } from '../LinkContributionDialog';
import { EmptyState } from '../EmptyState';
import { ReorderDialog } from '../ReorderDialog';

const useStyles = makeStyles(theme => ({
  linkItem: {
    paddingRight: theme.spacing(12),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  chip: {
    height: 20,
    fontSize: '0.7rem',
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  linkUrl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  openIcon: {
    fontSize: 14,
  },
}));

export interface TeamLinksSectionProps {
  defaultCollapsed?: boolean;
  editMode?: boolean;
  onHide?: () => void;
  /** Filter links to only show those associated with selected group refs */
  selectedGroupRefs?: string[];
  /** Whether the user chip is selected - shows user-specific links */
  userSelected?: boolean;
}

export const TeamLinksSection = ({
  defaultCollapsed = false,
  editMode = false,
  onHide,
  selectedGroupRefs,
  userSelected = true,
}: TeamLinksSectionProps) => {
  const classes = useStyles();
  const api = useApi(homepageLinksApiRef);

  const [links, setLinks] = useState<HomepageLink[]>([]);
  const [jwtGroups, setJwtGroups] = useState<string[]>([]);
  const [userRef, setUserRef] = useState<string | undefined>();
  const [groupOrder, setGroupOrder] = useState<string[] | undefined>();

  // Merge JWT-based groups with GitHub team refs from the teams bar so components
  // are aware of the user's groups even when the JWT ent claim is empty.
  const userGroups = useMemo(() => {
    const merged = new Set([...jwtGroups, ...(selectedGroupRefs || [])]);
    return Array.from(merged);
  }, [jwtGroups, selectedGroupRefs]);
  const [loading, setLoading] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [droppedUrl, setDroppedUrl] = useState<string | undefined>();
  const [droppedTitle, setDroppedTitle] = useState<string | undefined>();
  const [openedFromDrop, setOpenedFromDrop] = useState(false);
  const [editingLink, setEditingLink] = useState<HomepageLink | null>(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

  // Convert same-origin URLs to relative paths
  const convertToRelativeUrl = useCallback((url: string): string => {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      // Check if this URL is from the same Backstage instance
      if (parsedUrl.origin === window.location.origin) {
        // Return just the pathname + search + hash as a relative URL
        return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
      }
      return url;
    } catch {
      // If URL parsing fails, return as-is
      return url;
    }
  }, []);

  const handleLinkDrop = useCallback((url: string, title?: string) => {
    const processedUrl = convertToRelativeUrl(url);
    setDroppedUrl(processedUrl);
    setDroppedTitle(title);
    setOpenedFromDrop(true);
    setDialogOpen(true);
  }, [convertToRelativeUrl]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDroppedUrl(undefined);
    setDroppedTitle(undefined);
    setOpenedFromDrop(false);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [linksData, userInfo, prefs] = await Promise.all([
        api.getLinks('team', selectedGroupRefs),
        api.getUserInfo(),
        api.getPreferences(),
      ]);
      setLinks(linksData);
      setJwtGroups(userInfo.groups);
      setUserRef(userInfo.userRef);
      setGroupOrder(prefs.groupOrder);
      setIsHidden(prefs.hiddenSections?.includes('team-links') ?? false);
    } catch (err) {
      console.error('Failed to load team links:', err);
    } finally {
      setLoading(false);
    }
  }, [api, selectedGroupRefs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddLink = useCallback(async (input: any) => {
    await api.createLink(input);
    await loadData();
  }, [api, loadData]);

  const handleDeleteLink = useCallback(
    async (linkId: string) => {
      await api.deleteLink(linkId);
      await loadData();
      setEditingLink(null);
    },
    [api, loadData],
  );

  const handleHideSection = useCallback(async () => {
    const prefs = await api.getPreferences();
    const currentHidden = prefs.hiddenSections ?? [];
    if (!currentHidden.includes('team-links')) {
      await api.updatePreferences({
        hiddenSections: [...currentHidden, 'team-links'],
      });
      setIsHidden(true);
      onHide?.();
    }
  }, [api, onHide]);

  const handleSaveGroupOrder = useCallback(async (newOrder: string[]) => {
    await api.updatePreferences({ groupOrder: newOrder });
    setGroupOrder(newOrder);
  }, [api]);

  const formatGroupName = (groupRef: string) => {
    // Check if this is a personal link (user ref)
    if (groupRef.startsWith('user:')) {
      return 'Personal';
    }
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  // Get groups that actually have links in this section
  const groupsWithLinks = useMemo(() => {
    const allGroupsInLinks = new Set<string>();
    links.forEach(link => {
      link.groups.forEach(g => {
        if (userGroups.includes(g) || g === userRef) {
          allGroupsInLinks.add(g);
        }
      });
    });
    return userGroups.filter(g => allGroupsInLinks.has(g));
  }, [links, userGroups, userRef]);

  const hasPersonalLinks = useMemo(() => {
    return userRef ? links.some(link => link.groups.includes(userRef)) : false;
  }, [links, userRef]);

  // Sort links: personal links first, then by group order
  const sortedLinks = [...links].sort((a, b) => {
    const aIsPersonal = userRef && a.groups.includes(userRef);
    const bIsPersonal = userRef && b.groups.includes(userRef);

    // Personal links come first
    if (aIsPersonal && !bIsPersonal) return -1;
    if (!aIsPersonal && bIsPersonal) return 1;

    // If groupOrder is defined, sort by it
    if (groupOrder && groupOrder.length > 0) {
      const aMinOrder = Math.min(...a.groups.map(g => {
        const idx = groupOrder.indexOf(g);
        return idx === -1 ? Infinity : idx;
      }));
      const bMinOrder = Math.min(...b.groups.map(g => {
        const idx = groupOrder.indexOf(g);
        return idx === -1 ? Infinity : idx;
      }));
      return aMinOrder - bMinOrder;
    }

    return 0;
  });

  // Filter links by selected team group refs and userSelected using UNION logic
  // Show link if: (any selected team matches) OR (is user link AND userSelected)
  const filteredLinks = useMemo(() => {
    const noGroupsSelected = !selectedGroupRefs || selectedGroupRefs.length === 0;

    // If nothing selected, show nothing
    if (noGroupsSelected && !userSelected) {
      return [];
    }

    return sortedLinks.filter(link => {
      const isUserLink = userRef && link.groups.includes(userRef);
      const matchesUser = isUserLink && userSelected;
      const matchesTeam = !noGroupsSelected && link.groups.some(g => selectedGroupRefs!.includes(g));
      return matchesUser || matchesTeam;
    });
  }, [sortedLinks, selectedGroupRefs, userSelected, userRef]);

  if (loading) {
    return (
      <CollapsibleSection title="Team Links" sectionKey="team-links" editMode={editMode}>
        <Progress />
      </CollapsibleSection>
    );
  }

  if (isHidden) {
    return null;
  }

  if (userGroups.length === 0) {
    return (
      <CollapsibleSection
        title="Team Links"
        sectionKey="team-links"
        onHide={handleHideSection}
        editMode={editMode}
      >
        <EmptyState type="no-groups" />
      </CollapsibleSection>
    );
  }

  return (
    <>
      <CollapsibleSection
        title="Team Links"
        sectionKey="team-links"
        defaultCollapsed={defaultCollapsed}
        onHide={handleHideSection}
        onReorder={() => setReorderDialogOpen(true)}
        onLinkDrop={handleLinkDrop}
        isEmpty={filteredLinks.length === 0}
        emptyMessage="No team links for your groups yet."
        editMode={editMode}
        action={
          <Tooltip title="Add team link">
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={e => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              className={classes.addButton}
            >
              Add
            </Button>
          </Tooltip>
        }
      >
        {filteredLinks.length === 0 ? (
          <EmptyState
            type="no-links"
            linkType="team"
            onAddClick={() => setDialogOpen(true)}
          />
        ) : (
          <List dense>
            {filteredLinks.map(link => (
              <ListItem key={link.id} className={classes.linkItem}>
                <ListItemText
                  primary={
                    <Link
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.linkUrl}
                    >
                      {link.title}
                      <OpenInNewIcon className={classes.openIcon} />
                    </Link>
                  }
                  secondary={
                    <>
                      {link.description && (
                        <span>{link.description}</span>
                      )}
                      <div className={classes.chips}>
                        {link.groups
                          .filter(g => userGroups.includes(g))
                          .map(groupRef => (
                            <Chip
                              key={groupRef}
                              label={formatGroupName(groupRef)}
                              size="small"
                              className={classes.chip}
                              variant="outlined"
                            />
                          ))}
                      </div>
                    </>
                  }
                />
                {editMode && (
                  <ListItemSecondaryAction>
                    <Tooltip title="Edit link">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => setEditingLink(link)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </CollapsibleSection>

      <LinkContributionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleAddLink}
        linkType="team"
        userGroups={userGroups}
        userRef={userRef}
        initialUrl={droppedUrl}
        initialTitle={droppedTitle}
        preSelectMyself={openedFromDrop}
      />

      <LinkContributionDialog
        open={Boolean(editingLink)}
        onClose={() => setEditingLink(null)}
        onSubmit={handleAddLink}
        linkType="team"
        userGroups={userGroups}
        userRef={userRef}
        editingLink={editingLink}
        onDelete={handleDeleteLink}
      />

      <ReorderDialog
        open={reorderDialogOpen}
        onClose={() => setReorderDialogOpen(false)}
        onSave={handleSaveGroupOrder}
        groups={groupsWithLinks}
        userRef={hasPersonalLinks ? userRef : undefined}
        currentOrder={groupOrder}
      />
    </>
  );
};
